<template>
  <div id="monitoringPage" class="page white" ref="monitoringPage">
    <el-container class="content-aside">
      <el-main class="cpl-main">
        <div class="options">
          <el-row :gutter="20">
            <el-col :span="24" class="search-col">
              <!-- Player search bar -->
              <div class="workspace search">
                <div id="monitoringPlayerSearchBar" ref="mediaSearchBar" class="common-search">
                  <div class="search-bar-content">
                    <!-- Show search options -->
                    <div v-if="!showSearchInput" @click="handleSearchIconClick" class="search-icon show-search-options-action prevent-confirmed-search"></div>

                    <!-- Search Text -->
                    <div v-if="showSearchInput" onclick="event.stopPropagation()" class="search-content unconfirmed-search prevent-confirmed-search">
                      <el-input
                        id="player-monitoring-search"
                        :model-value="playerSearchTextVal"
                        @update:model-value="newValue => playerSearchTextVal = newValue"
                        ref="monitoringPlayerSearchInput"
                        :placeholder="$t('label.search')"
                        class="search-input">
                      </el-input>
                    </div>
                  </div>
                </div>
              </div>
            </el-col>
          </el-row>
        </div>

        <tag-slider v-if="doneFetchingTags && playerTags.length"
          :accessorComponentProp="'monitoringPage'"
          :tagsProp="playerTags"
          :tagsSelectionRequestProp="tagsSelectionRequest"
          @tag-slider-change="handleTagSliderChange($event)">
        </tag-slider>

        <el-row :gutter="0" class="main-content-row hide-scrollbar monitoring-map-row">
          <div id="monitoringMapWrapper">
            <!-- Map Places Search Bar -->
            <div v-if="doneFetchingAllPlayers" id="monitoringMapSearchWrapper">
              <input
                :placeholder="$t('label.addressSearchPlaceholder')"
                id="monitoringSearchPlacesInput"
                ref="monitoringSearchPlacesInput"
                class="controls cstm-input-grey"
                type="text" />
            </div>

            <div id="googleMapWrapper">
              <google-map v-if="doneFetchingAllPlayers"
              :playersProp="playersToShow"
              :playersAddressChangedProp="playersAddressChangedProp"
              :filterPlayerByIconColorProp="filterPlayerByIconColor"
              @google-map-change="handleGoogleMapChange($event)">
              </google-map>
            </div>
          </div>
        </el-row>
      </el-main>

      <el-aside class="cpl-aside">
        <div id="monitoringRightSideMenu" :class="['right-side-menu']">
          <div class="graph-wrapper"></div>

          <div v-if="showPlayerInfo" class="player-info">
            <template v-for="(optionProperty, indx) in optionProperties">
              <!-- Players Name -->
              <div v-if="optionProperty.id === optionPropertyIds.playerName" :key="indx" :class="['option-property', 'section-wrapper', optionProperty.id]">
                <div class="title">{{$t('label.name', 1)}}</div>
                <div class="content name">
                  <div class="presentation">{{selectedMarker.markerData.playersInfo.name}}</div>
                </div>
              </div>

              <!-- Serial Number -->
              <div v-if="optionProperty.id === optionPropertyIds.serialNumber" :key="indx" :class="['option-property', 'section-wrapper', optionProperty.id]">
                <div class="title">{{$t('label.serialNumber', 1)}}</div>
                <div class="content serialNumber">
                  <div class="presentation">{{selectedMarker.markerData.playersInfo.serialNumber}}</div>
                </div>
              </div>

              <!-- Firmware Version -->
              <div v-if="optionProperty.id === optionPropertyIds.firmwareVersion" :key="indx" :class="['option-property', 'section-wrapper', optionProperty.id]">
                <div class="title">{{$t('label.firmware', 1)}}</div>
                <div class="content firmware">
                  <div class="presentation">{{selectedMarker.markerData.playersInfo.firmwareVersion}}</div>
                </div>
              </div>

              <!-- Players Status -->
              <div v-if="optionProperty.id === optionPropertyIds.status" :key="indx" :class="['option-property', 'section-wrapper', optionProperty.id]">
                <div class="title">{{$t('label.status', 1)}}</div>
                <div class="content status">
                  <div class="presentation">{{selectedMarker.markerData.playersInfo._edit.status.label}}</div>
                  <div v-if="selectedMarker.markerData.playersInfo._edit.status.description" class="config-error-desc">{{selectedMarker.markerData.playersInfo._edit.status.description}}</div>
                </div>
              </div>

              <!-- Address -->
              <div v-if="optionProperty.id === optionPropertyIds.address" :key="indx" :class="['option-property', 'section-wrapper', optionProperty.id]">
                <div class="title">{{$t('label.address', 1)}}</div>
                <div @click="handleEditPlayersAddressClick" class="content address">
                  <span :class="['address-txt', {'show': !showPlayersAddressSearchBox}]">
                    {{`${selectedMarker.markerData.playersInfo._edit.address}`}}
                  </span>
                  <input id="playersAddressSearchBox" ref="playersAddressSearchBox" :class="['controls cstm-input-grey', {'show': showPlayersAddressSearchBox}]" :placeholder="''" type="text"/>
                </div>
              </div>
            </template>
          </div>

          <template v-if="!showPlayerInfo">
            <div v-if="countVisiblePlayers" class="count-summary-content">
              <div class="title">{{$t('label.player', 2)}}</div>

              <div id="countVisiblePlayersWrapper">
                <div class="count-visible-players-content">
                  <template v-if="countVisiblePlayers > 0">
                    <span class="count-all-visible-players">{{countVisiblePlayers}}</span>
                    <div @click="playersStatusSummaryIconClick({ all: true })" class="cover"></div>
                  </template>
                </div>
              </div>

              <div id="countAllPlayersWrapper">
                <div class="count-all-players-content">
                  <template v-if="countVisiblePlayers !== playersToShow.length">
                    <span class="count-all-players">{{playersToShow.length}}</span>
                    <div @click="playersStatusSummaryIconClick({ allPlayers: true })" class="cover"></div>
                  </template>
                </div>
              </div>

              <div id="countVisiblePlayersStatusWrapper">
                <div class="status-content">
                  <template v-for="(item, indx) in countVisiblePlayersStatusIcon.summary">
                    <div v-if="item" class="item" :key="indx">
                      <iframe :src="$utils.filterMediaPreviewUrl(item.iconSrc)" id="countVisiblePlayersStatusWrapperFrame" class="count-players-icon-frame small"></iframe>
                      <div @click="playersStatusSummaryIconClick(item)" class="cover"></div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </div>
      </el-aside>
    </el-container>
  </div>
</template>

<script src="./monitoring.js"></script>
<style lang="scss" src="./monitoring.scss"></style>
